import * as THREE from "three";
import { cheapTextures, normalMapFrom, roughFrom } from "./mapTextures.js";

/**
 * Procedural, tileable PROP surfaces — the sixth member of the family that
 * already covers the maps (`mapTextures`), the avatars (`avatarTextures`), the
 * guns (`weaponTextures`), the vehicles (`vehicleTextures`) and the creatures
 * (`monsterTextures`). Added 2026-08-01 (mission `graphics`, the PROP pass).
 *
 * The props are what is LEFT after eight graphics passes: the pickups you walk
 * into every thirty seconds on every map in every mode, the jump pads, the
 * purge containment doors and the castle drawbridge. Unlike a kaiju at 40 m
 * these are objects the player stands next to at ONE METRE — which is exactly
 * where `MeshStandardMaterial({ color })` on a `BoxGeometry` reads as a toy.
 *
 * Contract: same as `vehicleMaps`/`monsterMaps` and NOT `weaponMaps` — the
 * detail composites over WHITE and `params.color` is untouched, so a beige
 * crate and a purple crate share one albedo and the authored hue still tints.
 *
 * UVs: call sites run `worldProjectUV(geo, tile)` (metres per repeat), so the
 * planks on a 1.1 m chest and the planks on a 12 m drawbridge deck are the same
 * physical size — which per-face 0..1 UVs can never do, and which is most of
 * why the old props had no legible surface scale at all.
 */

const ALBEDO = 256;
const ROUGH = 128;

export type PropSurface =
  | "crate"   // sawn pine planking — the supply chests, the music crate
  | "oak"     // heavy weathered structural timber — the drawbridge deck
  | "steel"   // brushed painted metal, scratched — bands, gears, kiosks, casings
  | "tread"   // diamond anti-slip plate — jump pads, platforms
  | "weave"   // ballistic cordura twill — the armour vest, straps, rope
  | "enamel"  // glossy sprayed panel with scuffs and seams — medkit, signage
  | "hex";    // energy containment cells — the purge door field

interface Family {
  map: THREE.CanvasTexture | null;
  normal: THREE.CanvasTexture | null;
  rough: THREE.CanvasTexture | null;
}

function cvs(size: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  return { c, g: c.getContext("2d", { willReadFrequently: true })! };
}

/** Per-pixel grain straight into the ImageData — a fillRect per sample costs
 *  hundreds of ms in canvas state changes (same reason as `mapTextures`). */
function speckle(g: CanvasRenderingContext2D, n: number, alpha: number, lo: number, hi: number): void {
  const img = g.getImageData(0, 0, ALBEDO, ALBEDO);
  const d = img.data;
  for (let i = 0; i < n; i++) {
    const j = (((Math.random() * ALBEDO) | 0) + ((Math.random() * ALBEDO) | 0) * ALBEDO) * 4;
    const v = lo + Math.random() * (hi - lo);
    d[j] += (v - d[j]) * alpha;
    d[j + 1] += (v - d[j + 1]) * alpha;
    d[j + 2] += (v - d[j + 2]) * alpha;
  }
  g.putImageData(img, 0, 0);
}

/** Wandering line — grain, scratches, fibres. Drawn with wrap-around so the
 *  tile seam stays invisible. */
function wander(
  g: CanvasRenderingContext2D, x0: number, y0: number, len: number, ang: number,
  jitter: number, w: number, col: string,
): void {
  g.strokeStyle = col; g.lineWidth = w;
  g.beginPath();
  let x = x0, y = y0, a = ang;
  g.moveTo(x, y);
  const steps = Math.max(2, len / 4);
  for (let i = 0; i < steps; i++) {
    a += (Math.random() - 0.5) * jitter;
    x += Math.cos(a) * 4; y += Math.sin(a) * 4;
    g.lineTo(x, y);
  }
  g.stroke();
}

/** A dome of height — a rivet, a nail head, a boss. */
function dome(g: CanvasRenderingContext2D, x: number, y: number, r: number, hi: number, lo = 0): void {
  const gr = g.createRadialGradient(x - r * 0.3, y - r * 0.3, r * 0.05, x, y, r);
  gr.addColorStop(0, `rgba(255,255,255,${hi})`);
  gr.addColorStop(0.6, `rgba(150,150,150,${hi * 0.35})`);
  gr.addColorStop(1, `rgba(0,0,0,${lo})`);
  g.fillStyle = gr;
  g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
}

/** `a` draws the albedo (over white), `h` draws the height field (over grey). */
type Draw = (a: CanvasRenderingContext2D, h: CanvasRenderingContext2D) => void;

/** Longitudinal wood grain + a couple of knots, shared by both timbers. The
 *  grain runs along +X so a `worldProjectUV` plank reads with its length. */
function grain(a: CanvasRenderingContext2D, h: CanvasRenderingContext2D, lines: number, knots: number, dark: number): void {
  for (let i = 0; i < lines; i++) {
    const y = Math.random() * ALBEDO;
    const col = Math.random() < 0.5
      ? `rgba(70,44,24,${dark * (0.3 + Math.random() * 0.7)})`
      : `rgba(255,236,205,${0.10 + Math.random() * 0.16})`;
    wander(a, -8, y, ALBEDO + 16, 0, 0.05, 0.6 + Math.random() * 1.6, col);
    wander(h, -8, y, ALBEDO + 16, 0, 0.05, 1.0, "rgba(0,0,0,0.12)");
  }
  for (let k = 0; k < knots; k++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    for (let r = 12; r > 1.5; r -= 1.7) {
      a.strokeStyle = `rgba(52,30,14,${0.10 + (12 - r) * 0.03})`;
      a.lineWidth = 1.3;
      a.beginPath(); a.ellipse(x, y, r, r * 0.55, 0.4, 0, 7); a.stroke();
      h.strokeStyle = "rgba(0,0,0,0.18)"; h.lineWidth = 1.3;
      h.beginPath(); h.ellipse(x, y, r, r * 0.55, 0.4, 0, 7); h.stroke();
    }
  }
}

/** SAWN PINE CRATE — four planks across the tile with real gaps between them,
 *  bevelled plank edges, grain, and a nail head at each plank end. */
const drawCrate: Draw = (a, h) => {
  const P = 4, pitch = ALBEDO / P;
  grain(a, h, 240, 5, 0.5);
  for (let i = 0; i < P; i++) {
    const y = i * pitch;
    // the gap between planks: black in albedo, a deep trench in height
    a.fillStyle = "rgba(24,14,6,0.72)"; a.fillRect(0, y, ALBEDO, 3);
    h.fillStyle = "rgba(0,0,0,0.95)"; h.fillRect(0, y - 1, ALBEDO, 5);
    // bevelled shoulders either side of the gap
    a.fillStyle = "rgba(255,240,215,0.13)"; a.fillRect(0, y + 3, ALBEDO, 2);
    h.fillStyle = "rgba(255,255,255,0.42)"; h.fillRect(0, y + 4, ALBEDO, 3);
    // the plank crowns slightly toward its middle
    const gr = h.createLinearGradient(0, y + 5, 0, y + pitch);
    gr.addColorStop(0, "rgba(255,255,255,0.16)");
    gr.addColorStop(0.5, "rgba(128,128,128,0)");
    gr.addColorStop(1, "rgba(0,0,0,0.18)");
    h.fillStyle = gr; h.fillRect(0, y + 5, ALBEDO, pitch - 5);
    // nails, two per plank
    for (const nx of [26, ALBEDO - 26]) {
      dome(h, nx, y + pitch * 0.5, 3.4, 0.85, 0.35);
      dome(a, nx, y + pitch * 0.5, 3.4, 0.28, 0.30);
      a.fillStyle = "rgba(40,36,32,0.55)";
      a.beginPath(); a.arc(nx, y + pitch * 0.5, 2.4, 0, 7); a.fill();
    }
  }
  speckle(a, 16000, 0.10, 90, 200);
};

/** HEAVY WEATHERED TIMBER — two wide baulks per tile, split checks, iron
 *  staining round the bolts, and a much coarser grain than the crate. */
const drawOak: Draw = (a, h) => {
  const P = 2, pitch = ALBEDO / P;
  grain(a, h, 150, 3, 0.85);
  for (let i = 0; i < P; i++) {
    const y = i * pitch;
    a.fillStyle = "rgba(16,9,4,0.8)"; a.fillRect(0, y, ALBEDO, 5);
    h.fillStyle = "rgba(0,0,0,0.98)"; h.fillRect(0, y - 2, ALBEDO, 8);
    a.fillStyle = "rgba(230,205,170,0.11)"; a.fillRect(0, y + 5, ALBEDO, 3);
    h.fillStyle = "rgba(255,255,255,0.34)"; h.fillRect(0, y + 5, ALBEDO, 4);
  }
  // split checks running with the grain — the thing that says "old timber"
  for (let i = 0; i < 26; i++) {
    const y = Math.random() * ALBEDO, len = 40 + Math.random() * 120;
    const x = Math.random() * ALBEDO;
    wander(a, x, y, len, 0, 0.18, 2.0, "rgba(0,0,0,0.30)");
    wander(h, x, y, len, 0, 0.18, 2.6, "rgba(0,0,0,0.62)");
  }
  // iron bolts with rust bleed
  for (let i = 0; i < 6; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const bleed = a.createRadialGradient(x, y, 2, x, y, 16);
    bleed.addColorStop(0, "rgba(96,56,28,0.42)");
    bleed.addColorStop(1, "rgba(96,56,28,0)");
    a.fillStyle = bleed; a.beginPath(); a.arc(x, y, 16, 0, 7); a.fill();
    dome(h, x, y, 5, 0.9, 0.4);
    a.fillStyle = "rgba(38,34,30,0.8)"; a.beginPath(); a.arc(x, y, 4, 0, 7); a.fill();
  }
  speckle(a, 22000, 0.13, 70, 180);
};

/** BRUSHED PAINTED STEEL — a fine directional brush, a scatter of deeper
 *  scratches that catch the light, and rows of rivets. */
const drawSteel: Draw = (a, h) => {
  for (let i = 0; i < 1700; i++) { // the brush
    const y = Math.random() * ALBEDO;
    const lit = Math.random() < 0.5;
    a.strokeStyle = lit ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.09)";
    a.lineWidth = 0.5 + Math.random();
    a.beginPath(); a.moveTo(-4, y); a.lineTo(ALBEDO + 4, y + (Math.random() - 0.5) * 3); a.stroke();
  }
  for (let i = 0; i < 60; i++) { // scratches
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const ang = (Math.random() - 0.5) * 0.5;
    wander(a, x, y, 20 + Math.random() * 90, ang, 0.06, 1.1, "rgba(255,255,255,0.20)");
    wander(h, x, y, 20 + Math.random() * 90, ang, 0.06, 1.4, "rgba(0,0,0,0.35)");
  }
  // rivet rows top and bottom of the tile
  for (const y of [16, ALBEDO - 16]) {
    for (let x = 16; x < ALBEDO; x += 32) {
      dome(h, x, y, 5, 0.9, 0.35);
      dome(a, x, y, 5, 0.22, 0.22);
    }
  }
  // a shallow panel seam
  a.fillStyle = "rgba(0,0,0,0.30)"; a.fillRect(0, ALBEDO / 2 - 1, ALBEDO, 3);
  h.fillStyle = "rgba(0,0,0,0.75)"; h.fillRect(0, ALBEDO / 2 - 1, ALBEDO, 3);
  speckle(a, 9000, 0.07, 130, 235);
};

/** DIAMOND ANTI-SLIP PLATE — the raised-lozenge tread you stand on. Two
 *  alternating diagonals, hard-edged, plus dirt in the recesses. */
const drawTread: Draw = (a, h) => {
  const S = 42;
  a.fillStyle = "rgba(0,0,0,0.10)"; a.fillRect(0, 0, ALBEDO, ALBEDO);
  const lozenge = (cx: number, cy: number, ang: number): void => {
    for (const [g, hi, sh] of [[a, 0.30, 0.22] as const, [h, 0.95, 0.55] as const]) {
      g.save(); g.translate(cx, cy); g.rotate(ang);
      const gr = g.createLinearGradient(0, -6, 0, 6);
      gr.addColorStop(0, `rgba(255,255,255,${hi})`);
      gr.addColorStop(1, `rgba(0,0,0,${sh})`);
      g.fillStyle = gr;
      g.beginPath();
      g.moveTo(-15, 0); g.lineTo(0, -5); g.lineTo(15, 0); g.lineTo(0, 5); g.closePath();
      g.fill();
      g.restore();
    }
  };
  for (let ry = 0; ry * S < ALBEDO + S; ry++) {
    for (let rx = 0; rx * S < ALBEDO + S; rx++) {
      const ang = (rx + ry) % 2 ? 0.62 : -0.62;
      lozenge(rx * S + (ry % 2) * S * 0.5, ry * S, ang);
      lozenge(rx * S + (ry % 2) * S * 0.5 - ALBEDO, ry * S, ang); // wrap
    }
  }
  for (let i = 0; i < 40; i++) { // grime in the valleys
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 6 + Math.random() * 20;
    const gr = a.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, "rgba(20,20,24,0.22)");
    gr.addColorStop(1, "rgba(20,20,24,0)");
    a.fillStyle = gr; a.beginPath(); a.arc(x, y, r, 0, 7); a.fill();
  }
  speckle(a, 12000, 0.08, 90, 200);
};

/** BALLISTIC TWILL — a real over-under weave at two scales (the coarse cordura
 *  and the fine ripstop grid inside it), plus fibre fuzz. */
const drawWeave: Draw = (a, h) => {
  const T = 8;
  for (let y = 0; y < ALBEDO; y += T) {
    for (let x = 0; x < ALBEDO; x += T) {
      const over = ((x / T) + (y / T)) % 2 === 0;
      const gr = h.createLinearGradient(x, y, over ? x + T : x, over ? y : y + T);
      gr.addColorStop(0, "rgba(0,0,0,0.42)");
      gr.addColorStop(0.5, "rgba(255,255,255,0.55)");
      gr.addColorStop(1, "rgba(0,0,0,0.42)");
      h.fillStyle = gr; h.fillRect(x, y, T, T);
      const ga = a.createLinearGradient(x, y, over ? x + T : x, over ? y : y + T);
      ga.addColorStop(0, "rgba(0,0,0,0.16)");
      ga.addColorStop(0.5, "rgba(255,255,255,0.14)");
      ga.addColorStop(1, "rgba(0,0,0,0.16)");
      a.fillStyle = ga; a.fillRect(x, y, T, T);
    }
  }
  // ripstop grid: a heavier thread every 4th course
  for (let i = 0; i < ALBEDO; i += T * 4) {
    a.fillStyle = "rgba(0,0,0,0.13)"; a.fillRect(i, 0, 2, ALBEDO); a.fillRect(0, i, ALBEDO, 2);
    h.fillStyle = "rgba(255,255,255,0.28)"; h.fillRect(i, 0, 2, ALBEDO); h.fillRect(0, i, ALBEDO, 2);
  }
  for (let i = 0; i < 500; i++) { // fuzz
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    wander(a, x, y, 4 + Math.random() * 8, Math.random() * 7, 0.6, 0.5, "rgba(255,255,255,0.10)");
  }
  speckle(a, 14000, 0.07, 100, 190);
};

/** SPRAYED ENAMEL — nearly flat, which is the point: a glossy painted case
 *  next to a planked crate is the contrast that sells both. Orange-peel, a
 *  couple of panel seams, edge scuffs down to bare metal. */
const drawEnamel: Draw = (a, h) => {
  for (let i = 0; i < 260; i++) { // orange peel
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 4 + Math.random() * 12;
    dome(h, x, y, r, 0.10, 0.05);
  }
  for (const s of [0.34, 0.72]) { // seams
    const y = ALBEDO * s;
    a.fillStyle = "rgba(0,0,0,0.22)"; a.fillRect(0, y, ALBEDO, 2);
    h.fillStyle = "rgba(0,0,0,0.60)"; h.fillRect(0, y, ALBEDO, 2);
    a.fillStyle = "rgba(255,255,255,0.14)"; a.fillRect(0, y + 2, ALBEDO, 1);
  }
  for (let i = 0; i < 34; i++) { // scuffs
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    wander(a, x, y, 8 + Math.random() * 40, Math.random() * 7, 0.4, 1.0,
      Math.random() < 0.5 ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.12)");
    wander(h, x, y, 8 + Math.random() * 40, Math.random() * 7, 0.4, 1.0, "rgba(0,0,0,0.16)");
  }
  speckle(a, 6000, 0.05, 190, 250);
};

/** ENERGY CONTAINMENT CELLS — a hex lattice with bright cell walls and a
 *  darker interior. Used as the map AND the emissive map on the purge door,
 *  so the field has structure instead of being a flat blue pane. */
const drawHex: Draw = (a, h) => {
  a.fillStyle = "#101c26"; a.fillRect(0, 0, ALBEDO, ALBEDO);
  const R = 26, dy = R * 1.5, dx = R * Math.sqrt(3);
  const hexAt = (cx: number, cy: number): void => {
    for (const [g, wall, glow] of [[a, "rgba(190,250,255,0.95)", "rgba(70,190,240,0.20)"] as const,
                                   [h, "rgba(255,255,255,0.9)", "rgba(90,90,90,0)"] as const]) {
      g.beginPath();
      for (let i = 0; i < 6; i++) {
        const ang = Math.PI / 6 + (i / 6) * Math.PI * 2;
        const x = cx + Math.cos(ang) * R, y = cy + Math.sin(ang) * R;
        if (i) g.lineTo(x, y); else g.moveTo(x, y);
      }
      g.closePath();
      g.fillStyle = glow; g.fill();
      g.strokeStyle = wall; g.lineWidth = 2.4; g.stroke();
      // an inner ring so the cell reads as a chamber, not an outline
      g.strokeStyle = wall; g.lineWidth = 0.8; g.globalAlpha = 0.45;
      g.beginPath(); g.arc(cx, cy, R * 0.45, 0, 7); g.stroke();
      g.globalAlpha = 1;
    }
  };
  for (let row = -1; row * dy < ALBEDO + dy; row++) {
    for (let col = -1; col * dx < ALBEDO + dx; col++) {
      hexAt(col * dx + (row % 2 ? dx / 2 : 0), row * dy);
    }
  }
  // horizontal scan bands: the field is alive
  for (let i = 0; i < 5; i++) {
    const y = Math.random() * ALBEDO;
    a.fillStyle = "rgba(210,255,255,0.10)"; a.fillRect(0, y, ALBEDO, 3 + Math.random() * 6);
  }
};

const SPECS: Record<PropSurface, { draw: Draw; bump: number; roughLo: number; roughHi: number }> = {
  crate:  { draw: drawCrate,  bump: 2.6, roughLo: 0.62, roughHi: 0.96 },
  oak:    { draw: drawOak,    bump: 3.0, roughLo: 0.70, roughHi: 0.99 },
  steel:  { draw: drawSteel,  bump: 1.7, roughLo: 0.18, roughHi: 0.62 },
  tread:  { draw: drawTread,  bump: 3.2, roughLo: 0.34, roughHi: 0.82 },
  weave:  { draw: drawWeave,  bump: 2.2, roughLo: 0.66, roughHi: 0.97 },
  enamel: { draw: drawEnamel, bump: 0.9, roughLo: 0.08, roughHi: 0.34 },
  hex:    { draw: drawHex,    bump: 2.0, roughLo: 0.10, roughHi: 0.40 },
};

// Built INCREMENTALLY into a persistent partial map (one family per build) so
// the ~1.6 s full generation can be pumped a family at a time from an idle-time
// scheduler (see main.ts warmDeferredTextures) instead of freezing the UI in
// one block. `all()`/`propMaps`/`propAlbedo` are unchanged backstops that force
// whatever is still missing on demand; every path is idempotent + memoised.
const families: Partial<Record<PropSurface, Family>> = {};

function mk(canvas: HTMLCanvasElement, srgb: boolean): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.userData.shared = true; // module-cached: never disposed with a prop
  return t;
}

let buildMs = 0; // accumulated across incremental slices, for the one-time log
let warmLogged = false;

/** Generate (and cache) exactly ONE family — byte-identical to the old loop
 *  body (same draw / normal / roughness / `cheap` gate). */
function famOf(k: PropSurface): Family {
  const hit = families[k];
  if (hit) return hit;
  const t0 = performance.now();
  const cheap = cheapTextures();
  const spec = SPECS[k];
  const { c: ac, g: ag } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  ag.fillStyle = "#ffffff"; ag.fillRect(0, 0, ALBEDO, ALBEDO); // detail over WHITE: colour still tints
  hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  spec.draw(ag, hg);
  const fam: Family = {
    map: mk(ac, true),
    normal: cheap ? null : mk(normalMapFrom(hc, spec.bump, ALBEDO), false),
    rough: cheap ? null : mk(roughFrom(hc, spec.roughLo, spec.roughHi, ROUGH), false),
  };
  families[k] = fam;
  buildMs += performance.now() - t0;
  if (!warmLogged && (Object.keys(SPECS) as PropSurface[]).every((s) => families[s])) {
    warmLogged = true;
    console.log(`[proptex] ${Object.keys(SPECS).length} prop families warm in ${buildMs.toFixed(0)} ms`);
  }
  return fam;
}

function all(): Record<PropSurface, Family> {
  for (const k of Object.keys(SPECS) as PropSurface[]) famOf(k);
  return families as Record<PropSurface, Family>;
}

/** GPU-upload every prop family texture NOW (under the load screen), given the
 *  renderer's warm callback. A pickup respawns on a 20 s timer and a purge door
 *  is bought mid-round, so first draw is mid-match; move the upload here.
 *  Idempotent; cycle-free (the callback comes from the renderer). */
export function warmPropTextures(warm: (t: THREE.Texture) => void): void {
  const fams = all();
  for (const k of Object.keys(fams) as PropSurface[]) {
    const f = fams[k];
    if (f.map) warm(f.map);
    if (f.normal) warm(f.normal);
    if (f.rough) warm(f.rough);
  }
}

/** Build ONE not-yet-generated family and return whether any still remain, so
 *  an idle scheduler can pump the ~1.6 s job slice-by-slice and keep the menu
 *  responsive. No-op (returns false) once everything is warm. */
export function warmOnePropFamily(): boolean {
  const keys = Object.keys(SPECS) as PropSurface[];
  const next = keys.find((k) => !families[k]);
  if (next === undefined) return false;
  famOf(next);
  return keys.some((k) => !families[k]);
}

/** Shared normalScale vectors — never allocated per material. */
const normalScales = new Map<string, THREE.Vector2>();

/**
 * Fold a prop surface's albedo/normal/roughness into MeshStandardMaterial
 * params. Like `monsterMaps` (and unlike `weaponMaps`) this does NOT touch
 * `params.color`.
 */
export function propMaps(
  kind: PropSurface,
  params: THREE.MeshStandardMaterialParameters,
  strength = 1,
): THREE.MeshStandardMaterialParameters {
  const fam = famOf(kind);
  params.map = fam.map ?? undefined;
  if (fam.normal) {
    params.normalMap = fam.normal;
    const sk = `${kind}|${strength}`;
    let s = normalScales.get(sk);
    if (!s) { s = new THREE.Vector2(strength, strength); normalScales.set(sk, s); }
    params.normalScale = s;
  }
  if (fam.rough) params.roughnessMap = fam.rough;
  // REFLECTIONS (2026-10-21 `graphics`): the metal/glazed props (steel bands,
  // tread plate, enamel signage, hex cells) pick up the scene env probe so they
  // read as real surfaces, not flat paint. Same deal as vehicleMaps — free
  // (probe already built), no-op on low-spec, and the matte families (crate/oak
  // timber, cordura weave) are intentionally left out.
  const reflect = PROP_REFLECT[kind];
  if (reflect !== undefined) params.envMapIntensity ??= reflect;
  return params;
}

/** Per-surface env-reflection strength (see propMaps). Timber/fabric are absent
 *  on purpose — they stay matte. */
const PROP_REFLECT: Partial<Record<PropSurface, number>> = {
  steel: 1.6, tread: 1.5, enamel: 1.7, hex: 1.5,
};

/** The `hex` albedo on its own — the purge door uses it as an emissiveMap as
 *  well as a map, so the cell walls glow rather than the whole pane. */
export function propAlbedo(kind: PropSurface): THREE.Texture | null {
  return famOf(kind).map;
}

/** Build every family now. Called from the map-load warm-up: a pickup is
 *  spawned the instant a round starts, which is the worst possible moment to
 *  build seven canvases. */
export function prewarmPropTextures(): void { all(); }
